package com.example.readingnotes.domain.model

import kotlinx.datetime.Instant

/**
 * Purchasable content for paid articles
 */
data class PurchasableContent(
    val id: String, // UUID
    val postId: String, // UUID
    val tenantId: String, // UUID
    val creatorId: String, // UUID
    val price: Int, // Price in currency units (e.g., JPY)
    val currency: String, // e.g., "JPY", "USD"
    val status: PurchasableContentStatus, // "draft", "active", "inactive"
    val publishedAt: Instant? = null,
    val createdAt: Instant,
    val updatedAt: Instant,
)

/**
 * Content purchase information
 */
data class ContentPurchase(
    val id: String, // UUID
    val contentId: String, // UUID
    val purchaserId: String, // UUID
    val amountPaid: Int,
    val platformFee: Int,
    val creatorEarnings: Int,
    val purchasedAt: Instant,
    val refundedAt: Instant? = null,
)

/**
 * Purchase session for Stripe Checkout
 */
data class PurchaseSession(
    val checkoutUrl: String,
    val sessionId: String,
    val contentId: String,
    val amount: Int,
    val currency: String,
)

/**
 * Content access check result
 */
data class ContentAccessResult(
    val hasAccess: Boolean,
    val isPurchased: Boolean,
    val isAuthor: Boolean,
    val price: Int? = null,
    val currency: String? = null,
    val purchaseDate: Instant? = null,
)

/**
 * Sales dashboard data
 */
data class SalesDashboard(
    val totalSales: Int,
    val totalRevenue: Int,
    val platformFees: Int,
    val creatorEarnings: Int,
    val salesByContent: List<ContentSales>,
)

/**
 * Sales per content
 */
data class ContentSales(
    val contentId: String,
    val postId: String,
    val title: String, // Post title
    val salesCount: Int,
    val revenue: Int,
)

/**
 * Purchasable content status
 */
enum class PurchasableContentStatus {
    DRAFT,
    ACTIVE,
    INACTIVE,
}

/**
 * Convert string status to enum
 */
fun String.toPurchasableContentStatus(): PurchasableContentStatus? =
    when (this.lowercase()) {
        "draft" -> PurchasableContentStatus.DRAFT
        "active" -> PurchasableContentStatus.ACTIVE
        "inactive" -> PurchasableContentStatus.INACTIVE
        else -> null
    }

/**
 * Convert enum to string
 */
fun PurchasableContentStatus.toStringValue(): String =
    when (this) {
        PurchasableContentStatus.DRAFT -> "draft"
        PurchasableContentStatus.ACTIVE -> "active"
        PurchasableContentStatus.INACTIVE -> "inactive"
    }
